import { IDocument, IDocumentType } from 'API/documents';
import {
  IDocumentFilter,
  IDocuments,
  IExtendedDocument,
} from 'Context/DocumentsContext';
import {
  differenceInDays,
  differenceInYears,
  isThisMonth,
  isThisWeek,
  isThisYear,
  isToday,
} from 'date-fns';
import { DOCUMENTS_REDUCER_CASE } from 'Enum/documents';

const LAST_WEEK_DAYS = 7,
  LAST_MONTH_DAYS = 30,
  LAST_YEAR = 1;

export interface IAction {
  type: string;
  payload: {
    value: any;
    types?: any;
    filter?: IDocumentFilter;
  };
}

export function DocumentsReducer(state: IDocuments, action: IAction) {
  switch (action.type) {
    case DOCUMENTS_REDUCER_CASE.INIT: {
      const extendedDocs = extendDocumentsDTO(
        action.payload.value.docs,
        action.payload.value.types
      );
      const results = sortDocumentsByDayWeekMonthYear(extendedDocs);
      return { ...state, results };
    }
    case DOCUMENTS_REDUCER_CASE.INIT_MOBILE: {
      const previousYearsResults = extendDocumentsDTO(
        action.payload.value.docs,
        action.payload.value.types
      );
      return { ...state, previousYearsResults };
    }
    case DOCUMENTS_REDUCER_CASE.INIT_PAST_YEARS: {
      const previousYearX = extendDocumentsDTO(
        action.payload.value.docs,
        action.payload.value.types
      );
      return {
        ...state,
        previousYearsResults: [...state.previousYearsResults, ...previousYearX],
      };
    }
    case DOCUMENTS_REDUCER_CASE.FILTER: {
      const filteredResults = advancedFilter(
        action.payload.value.filter,
        state
      );
      return { ...state, filteredResults };
    }
    default:
      return state;
  }
}

/**
 * Fix function for the received DTO in order to avoid huge data management on server side.
 * @param docs An array of `IDocument` objects.
 * @param types An array of `IDocumentType` objects.
 * @returns An extended IDocument according to the IExtendedDocument.
 */
function extendDocumentsDTO(docs: IDocument[], types: IDocumentType[]) {
  const extendedDocs: IExtendedDocument[] = [];
  for (const doc of docs) {
    const docType = types.find((docType) => docType.id === doc.typeId);
    if (docType) {
      extendedDocs.push({
        ...doc,
        isSentToUser: docType.isSentToUser,
        infoDescription: docType.infoDescription,
        infoLinkText: docType.infoLinkText,
        infoLink: docType.infoLink,
        documentSubject: docType.subject,
      });
    }
  }
  return extendedDocs;
}

/**
 * Sorts an array of documents into categories based on their creation date:
 * - Documents created today
 * - Documents created this week
 * - Documents created this month
 * - Documents created this year
 *
 * @param documents An array of `IExtendedDocument` objects.
 * @returns A sorted object with the 4 fields above.
 */
function sortDocumentsByDayWeekMonthYear(documents: IExtendedDocument[]) {
  const todayDocs: IExtendedDocument[] = [],
    thisWeekDocs: IExtendedDocument[] = [],
    thisMonthDocs: IExtendedDocument[] = [],
    thisYearDocs: IExtendedDocument[] = [];

  for (const doc of documents) {
    const docDate = new Date(doc.documentDate);
    if (isToday(docDate)) todayDocs.push(doc);
    else if (isThisWeek(docDate)) thisWeekDocs.push(doc);
    else if (isThisMonth(docDate)) thisMonthDocs.push(doc);
    else if (isThisYear(docDate)) thisYearDocs.push(doc);
  }
  return { todayDocs, thisWeekDocs, thisMonthDocs, thisYearDocs };
}

function advancedFilter(filter: IDocumentFilter, documents: IDocuments) {
  const {
    searchByNameOrSubject,
    subjectsTypes,
    isDocumentSentByUser,
    datePeriod,
    orderByDescending,
  } = filter;

  const allDocs: IExtendedDocument[] = [
    ...documents.results.todayDocs,
    ...documents.results.thisWeekDocs,
    ...documents.results.thisMonthDocs,
    ...documents.results.thisYearDocs,
    ...documents.previousYearsResults,
  ];

  const sortByNameOrSubject =
    searchByNameOrSubject.length > 0
      ? allDocs.filter(
          (doc: IExtendedDocument) =>
            doc.name?.includes(searchByNameOrSubject) ||
            doc.subjectName?.includes(searchByNameOrSubject)
        )
      : allDocs;

  const sortBySubjectTypes =
    subjectsTypes.length > 0
      ? sortByNameOrSubject.filter((doc: IExtendedDocument) =>
          subjectsTypes.includes(doc.subjectName)
        )
      : sortByNameOrSubject;

  const sortBySendOrReceivedByUser =
    isDocumentSentByUser !== null
      ? sortBySubjectTypes.filter(
          (doc: IExtendedDocument) => doc.isSentToUser === isDocumentSentByUser
        )
      : sortBySubjectTypes;

  const sortByDatePeriod =
    datePeriod.lastWeek || datePeriod.lastMonth || datePeriod.lastYear
      ? sortBySendOrReceivedByUser.filter((doc: IExtendedDocument) => {
          const today = new Date();
          // Checks if a document falls within the specified time range (last week, last month, or last year).
          if (
            (datePeriod.lastWeek &&
              differenceInDays(today, new Date(doc.documentDate)) <=
                LAST_WEEK_DAYS) ||
            (datePeriod.lastMonth &&
              differenceInDays(today, new Date(doc.documentDate)) <=
                LAST_MONTH_DAYS) ||
            (datePeriod.lastYear &&
              differenceInYears(today, new Date(doc.documentDate)) <= LAST_YEAR)
          )
            return true;
          return false;
        })
      : sortBySendOrReceivedByUser;

  const sortByOrderByNewOld = orderByDescending
    ? sortByDatePeriod
    : sortByDatePeriod.reverse();

  return sortByOrderByNewOld;
}
